/**
 * A central de alertas.
 *
 * **O SSE nao leva detalhe.** A API manda `id`, `kind` e `criado_em`, e nada
 * mais -- e isso e uma decisao do projeto, nao uma limitacao: um alerta com
 * `title` e `body` na stream seria o mesmo dado que o push recusa levar. Por
 * isso, quando chega um evento, a tela **recarrega a central**: ela descobre
 * *que* chegou, e o detalhe vem da chamada que exige sessao.
 *
 * **A stream fecha quando a tela sai.** Cada stream segura uma conexao do pool
 * da API, e uma aba com a central aberta continuaria gastando conexao depois de
 * a pessoa ter ido para outra tela.
 */

import { central as buscarCentral, ErroDaApi, marcarLido, marcarTodosLidos } from "./api";
import { abrirStream } from "./sse";
import { telaDaCentral, texto } from "./telas";
import type { AlertaParaTela } from "./telas";

let raiz: HTMLElement | null = null;
let fecharStream: (() => void) | null = null;
/** Quantos alertas a pessoa leu nesta sessao da tela, para o aviso de "lidos". */
let recemMarcados = 0;

export function definirRaiz(elemento: HTMLElement): void {
  raiz = elemento;
}

/** Fecha a stream. **Chamado quando a pessoa sai da tela.** */
export function fechar(): void {
  fecharStream?.();
  fecharStream = null;
}

/** O rotulo de cada tipo de alerta, em portugues de gente. */
export function rotuloDoKind(kind: string): string {
  const rotulos: Record<string, string> = {
    geofence_entrada: "Entrou na cerca",
    geofence_saida: "Saiu da cerca",
    localizacao_atendida: "Respondeu o pedido",
    localizacao_expirada: "Nao respondeu o pedido",
    saldo_baixo: "Saldo baixo",
    documento_vencendo: "Documento vencendo",
    falha_ocr: "Falha no OCR",
    falha_backup: "Falha no backup",
    disco_alto: "Disco quase cheio",
  };
  return rotulos[kind] ?? kind;
}

export async function desenhar(): Promise<void> {
  if (raiz === null) {
    return;
  }
  const dados = await buscarCentral();
  const alertas: AlertaParaTela[] = dados.alertas.map((alerta) => ({
    id: alerta.id,
    kind: alerta.kind,
    rotulo: rotuloDoKind(alerta.kind),
    title: alerta.title,
    body: alerta.body,
    criada_em: alerta.criada_em,
    lido: alerta.lido,
    status: alerta.status,
  }));

  raiz.innerHTML = telaDaCentral(alertas, dados.nao_lidos, recemMarcados);
  raiz.classList.remove("conteudo--centrado");
  liga();
}

/** Liga os botoes. **Toda vez que a tela e desenhada.** */
function liga(): void {
  if (raiz === null) {
    return;
  }

  for (const botao of raiz.querySelectorAll<HTMLButtonElement>("[data-marcar-lido]")) {
    botao.addEventListener("click", () => {
      const id = botao.dataset["marcarLido"] ?? "";
      void executa(async () => {
        await marcarLido(id);
        recemMarcados += 1;
        await desenhar();
      });
    });
  }

  raiz.querySelector<HTMLButtonElement>("#marcar-todos")?.addEventListener("click", () => {
    void executa(async () => {
      const marcados = await marcarTodosLidos();
      recemMarcados += marcados.marcados;
      await desenhar();
    });
  });
}

/**
 * Abre a stream da central.
 *
 * **A stream e aberta uma vez**, e nao a cada redesenho: redesenhar a tela
 * happens quando um alerta chega, e abrir uma stream por redesenho deixaria
 * varias conexoes abertas ao mesmo tempo -- cada uma segurando uma sessao do
 * pool da API.
 */
export function abrir(): void {
  fechar();
  fecharStream = abrirStream("/api/alertas/stream", ["alerta"], () => {
    // **Um evento so recarrega a central.** O evento nao tem o detalhe do
    // alerta, entao a unica forma de mostrar e buscar de novo.
    void desenhar().catch(() => undefined);
  });
}

async function executa(acao: () => Promise<void>): Promise<void> {
  try {
    await acao();
  } catch (erro) {
    const area = raiz?.querySelector<HTMLElement>("#aviso-da-central");
    if (area === null || area === undefined) {
      return;
    }
    area.hidden = false;
    area.textContent = erro instanceof ErroDaApi ? erro.message : "Algo deu errado.";
  }
}

export { texto };
